import { useMemo, useRef } from "react"
import type { RefObject } from "react"

import { useBoolean, useInterval } from "react-use"

import { seconds } from "@/3514/utils"

import utils from "./use-session-time-state.utils"

const { getIsSessionPast, getIsSessionLive } = utils

type TUseSessionTimeStateParams = { startDate: string; durationInMins: number; isEnabled: boolean }

type TSessionTimeState = { isLive: boolean; isPast: boolean; isUpcoming: boolean; wasLive: boolean }

function useSessionTimeState(
    params: TUseSessionTimeStateParams
): ReturnType<(params: TUseSessionTimeStateParams) => TSessionTimeState> {
    const [isLive, setIsLive]: ReturnType<typeof useBoolean> = useBoolean(
        params.isEnabled ? getIsSessionLive(params) : false
    )

    const wasLiveRef: RefObject<boolean> = useRef<boolean>(false)

    const isPast: boolean = getIsSessionPast(params)

    useInterval((): void => {
        if (!params.isEnabled) return

        if (isPast) return

        const _next: boolean = getIsSessionLive(params)

        if (_next !== isLive) setIsLive(_next)

        if (_next === false) wasLiveRef.current = true
    }, seconds(30))

    return useMemo(
        (): TSessionTimeState => ({ isLive, isPast, isUpcoming: !isPast || isLive, wasLive: wasLiveRef.current }),
        [isLive, isPast]
    )
}

export { useSessionTimeState }
export { utils as sessionTimeStateUtils }
export type { TSessionTimeState, TUseSessionTimeStateParams }
